import { Injectable, signal } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, Subject, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { ApiService } from './api.service';
import { SessionService } from './session.service';
import {
  AddToCartRequest,
  AddToCartResponse,
  Cart,
  CartItem,
} from '../models/cart.interface';

interface PersistedCart {
  /** The session ID this cart belongs to. Used to invalidate stale carts. */
  sessionId: string;
  cart: Cart;
}

@Injectable({
  providedIn: 'root',
})
export class CartStateService {
  /**
   * Storage key for the persisted cart. We use localStorage so the cart
   * survives reloads and tab navigation.
   */
  private static readonly CART_STORAGE_KEY = 'hypenationCart';

  private cartUpdateSubject = new Subject<void>();
  public cartUpdate$ = this.cartUpdateSubject.asObservable();

  /**
   * Reactive accessor for the currently resolved cart. Components can read
   * this to display badge counts, navigate to /cart/{uid}, etc.
   */
  private readonly _cart = signal<Cart | null>(null);
  public readonly cart = this._cart.asReadonly();

  private inFlightResolve$: Observable<Cart | null> | null = null;

  constructor(
    private readonly _apiService: ApiService,
    private readonly _sessionService: SessionService
  ) {
    this.hydrateFromStorage();
    // When SessionService rotates the session (via the boot guard or any
    // other trigger), drop the cached cart so the next resolve hits a
    // fresh session.
    this._sessionService.sessionRotation$.subscribe(() => this.clearCart());
  }

  /**
   * Resolve the active cart for the current session and cache the result.
   *
   * Resolution order:
   *   1. In-memory signal (warm cache).
   *   2. Hydrated entry from localStorage (survives reloads).
   *   3. In-flight HTTP request shared across concurrent callers.
   *   4. A fresh `getCartBySessionId` call.
   *
   * Pass `forceRefresh = true` to skip the cache and re-hit the backend.
   */
  public resolveCart(forceRefresh = false): Observable<Cart | null> {
    // Gate every resolve on the pending-order boot guard. The guard is
    // memoized inside SessionService, so subsequent calls are cheap; the
    // first call in a session may rotate state before we read the cache
    // below, which is exactly what makes this race-safe.
    return this._sessionService.verifyPendingOrder().pipe(
      switchMap(() => {
        const cached = this._cart();
        if (!forceRefresh && cached) {
          return of(cached);
        }

        if (this.inFlightResolve$) {
          return this.inFlightResolve$;
        }

        this.inFlightResolve$ = this._sessionService.getSessionId().pipe(
          switchMap((id) => {
            if (!id) {
              return of(null);
            }
            return this._apiService.getCartBySessionId(id).pipe(
              map((res): { id: string; cart: Cart | null } => ({
                id,
                cart: res.body ?? null,
              }))
            );
          }),
          switchMap((result) => {
            if (!result || !result.cart) {
              return of(null);
            }
            const { id, cart } = result;
            // The backend has been observed returning a stale
            // `numberOfItems` that disagrees with the items endpoint
            // (e.g. 9 reported vs 1 actual). Reconcile against the
            // authoritative items list before emitting, but only when
            // the reported count is non-zero — a zero is trusted so
            // the empty-cart fast path (post session rotation) stays
            // at a single round-trip.
            if ((cart.numberOfItems ?? 0) === 0) {
              this.updateCart(cart, id);
              return of(cart);
            }
            return this._apiService.getCartItemsByUuid(cart.uid).pipe(
              map((itemsRes) => {
                const items = itemsRes.body ?? [];
                const normalized = this.sumItemQuantities(items);
                const reconciled: Cart = {
                  ...cart,
                  numberOfItems: normalized,
                };
                this.updateCart(reconciled, id);
                return reconciled;
              }),
              catchError((err) => {
                // Reconciliation is a shim — never block the resolve.
                console.warn(
                  'Cart items reconciliation failed; emitting unreconciled cart',
                  err
                );
                this.updateCart(cart, id);
                return of(cart);
              })
            );
          }),
          catchError((err) => {
            console.error('Failed to resolve cart:', err);
            return of(null);
          }),
          finalize(() => {
            this.inFlightResolve$ = null;
          }),
          shareReplay(1)
        );

        return this.inFlightResolve$;
      })
    );
  }

  /**
   * Refresh the cached cart from the backend (e.g. after add/remove).
   */
  public refreshCart(): Observable<Cart | null> {
    return this.resolveCart(true);
  }

  // ────────────────────────────────────────────────────────────────────
  // Stale-cart recovery
  // ────────────────────────────────────────────────────────────────────

  /**
   * Detect the backend "the cached cart UID no longer exists" failure.
   *
   * Observed wire format (dev, 2026-06-09); because `addToCart` uses
   * `responseType: 'text'`, the body arrives in `err.error` as a raw JSON
   * string:
   *   { status: 500, type: 'RecordNotFoundException',
   *     error: ['Cart record was not found'],
   *     message: 'Cart record was not found' }
   *
   * Rule: an `HttpErrorResponse` with status 404 or 500, whose parsed body
   * either carries `type === 'RecordNotFoundException'` or matches
   * `/record not found/i`, AND whose message/error text mentions the cart.
   * The URL is intentionally NOT used for the cart check — the add-items URL
   * always contains "cart", so it cannot separate a cart-not-found from a
   * product-not-found (which reads "Product record was not found").
   */
  private isStaleCartError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) {
      return false;
    }
    if (err.status !== 404 && err.status !== 500) {
      return false;
    }

    let body: unknown = err.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        // Not JSON — fall back to matching the raw string below.
      }
    }

    const record =
      body && typeof body === 'object'
        ? (body as Record<string, unknown>)
        : null;

    // Text used both for the not-found wording and the cart scoping. Prefer
    // the structured message/error fields; fall back to the whole body string.
    const haystack = record
      ? [record['message'], record['error']]
          .map((v) => (typeof v === 'string' ? v : JSON.stringify(v ?? '')))
          .join(' ')
      : String(body ?? '');

    const isNotFound =
      record?.['type'] === 'RecordNotFoundException' ||
      /record\s*not\s*found/i.test(haystack);
    const mentionsCart = /cart/i.test(haystack);

    return isNotFound && mentionsCart;
  }

  /**
   * Rotate the session and resolve a fresh cart. Shared by the add-to-cart
   * recovery wrapper and the component's null-cart submit path.
   */
  public rotateAndResolve(): Observable<Cart | null> {
    this._sessionService.rotateSession();
    return this.resolveCart(true);
  }

  /**
   * Add to cart with single-shot stale-cart recovery. On a stale-cart error
   * (the cached UID was collected by the backend), rotate the session, resolve
   * a fresh empty cart, and retry the add exactly once against the new UID.
   * Any other error — or a second stale-cart error on the retry — is forwarded
   * untouched. On success the navbar badge is refreshed via notifyCartUpdate().
   */
  public addToCartWithRecovery(
    payload: AddToCartRequest,
    cartUid: string
  ): Observable<HttpResponse<AddToCartResponse>> {
    return this._apiService.addToCart(payload, cartUid).pipe(
      catchError((err) => {
        if (!this.isStaleCartError(err)) {
          return throwError(() => err);
        }
        console.warn('Stale cart recovered after not-found error', {
          oldCartUid: cartUid,
        });
        return this.rotateAndResolve().pipe(
          take(1),
          switchMap((fresh) => {
            if (!fresh) {
              // Rotation could not produce a cart — surface the original error.
              return throwError(() => err);
            }
            return this._apiService.addToCart(payload, fresh.uid);
          })
        );
      }),
      tap((res) => this.syncCartFromAddResponse(res))
    );
  }

  /**
   * Update the cached cart from an add-items success response. The add-items
   * endpoint returns the authoritative `{ cart, cartItem }` — using it makes
   * the cached cart (and its UID) correct synchronously, which matters after a
   * recovery rotation where the cart UID has changed. Because the request uses
   * `responseType: 'text'`, the emitted value is a raw JSON string. If parsing
   * fails we fall back to a full `notifyCartUpdate()` refetch.
   */
  private syncCartFromAddResponse(res: unknown): void {
    let body: unknown = res;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        body = null;
      }
    }
    const cart =
      body && typeof body === 'object'
        ? (body as { cart?: Cart }).cart
        : undefined;

    if (cart?.uid) {
      // Set immediately so the UID is correct synchronously (callers read the
      // signal right after the add), then reconcile the count below.
      this.setCart(cart);
      this.cartUpdateSubject.next();
      // The add-items response `numberOfItems` shares the same staleness the
      // resolve path guards against, so reconcile it against the authoritative
      // items endpoint (non-zero only — a zero is trusted).
      this.reconcileCartCount(cart);
    } else {
      // Could not read the cart from the response — fall back to a refetch.
      this.notifyCartUpdate();
    }
  }

  /**
   * Reconcile a cart's `numberOfItems` against the authoritative items endpoint
   * and update the cached cart if it differs. No-op for a zero count (trusted)
   * or if the cached cart has changed UID before the items response lands.
   * Failures are swallowed — reconciliation is a shim, never a blocker.
   */
  private reconcileCartCount(cart: Cart): void {
    if ((cart.numberOfItems ?? 0) === 0) {
      return;
    }
    this._apiService
      .getCartItemsByUuid(cart.uid)
      .pipe(
        map((itemsRes) => this.sumItemQuantities(itemsRes.body ?? [])),
        catchError((err) => {
          console.warn('Cart count reconciliation failed after add', err);
          return of(null);
        })
      )
      .subscribe((normalized) => {
        if (normalized === null) {
          return;
        }
        const current = this._cart();
        if (!current || current.uid !== cart.uid) {
          return;
        }
        if (current.numberOfItems === normalized) {
          return;
        }
        this.setCart({ ...current, numberOfItems: normalized });
        this.cartUpdateSubject.next();
      });
  }

  /** Sum item quantities, clamping negatives to zero. */
  private sumItemQuantities(items: CartItem[]): number {
    return items.reduce((sum, item) => sum + Math.max(0, item.quantity ?? 0), 0);
  }

  /**
   * Clear-then-add with stale-cart recovery, for the "Different Event in Cart"
   * flow. A stale-cart error from clearCart is success-equivalent (a cart the
   * backend cannot find is already empty), so we swallow it and fall through to
   * addToCartWithRecovery, which owns the single rotation if the UID is still
   * stale. At most one rotation occurs across the whole action.
   */
  public clearCartAndAddWithRecovery(
    payload: AddToCartRequest,
    cartUid: string
  ): Observable<HttpResponse<AddToCartResponse>> {
    return this._apiService.clearCart(cartUid).pipe(
      catchError((err) => {
        if (this.isStaleCartError(err)) {
          return of(null);
        }
        return throwError(() => err);
      }),
      switchMap(() => this.addToCartWithRecovery(payload, cartUid))
    );
  }

  /**
   * Update the cached cart synchronously (e.g. when a mutation endpoint
   * returns the new cart in its response).
   */
  public setCart(cart: Cart | null): void {
    if (cart) {
      const sessionId = localStorage.getItem('hypenationSessionId');
      if (sessionId) {
        this.updateCart(cart, sessionId);
      } else {
        // No session bound yet — update the in-memory signal only and skip
        // persistence. Writing with an empty-string sessionId would later
        // defeat the hydration invalidation check, smuggling a stale cart
        // past a session rotation on reload.
        this._cart.set(cart);
      }
    } else {
      this.clearCart();
    }
  }

  public clearCart(): void {
    this._cart.set(null);
    this.inFlightResolve$ = null;
    this.removeFromStorage();
    // Emit so subscribers (e.g. the navbar badge) re-read and reflect the
    // empty state without waiting for the user's next action.
    this.cartUpdateSubject.next();
  }

  /**
   * Notify all subscribers that the cart has been updated. Also refreshes
   * the cached cart from the backend so the badge count is accurate.
   */
  notifyCartUpdate(): void {
    this.refreshCart().subscribe({
      next: () => this.cartUpdateSubject.next(),
      error: () => this.cartUpdateSubject.next(),
    });
  }

  /**
   * Reconcile cached cart count using the cart items currently rendered in UI.
   * This keeps the navbar badge in sync when backend cart totals lag behind.
   */
  public syncCartItemCount(cartUid: string, cartItems: CartItem[]): void {
    const current = this._cart();
    if (!current || current.uid !== cartUid) {
      return;
    }

    const normalizedCount = this.sumItemQuantities(cartItems);

    if (current.numberOfItems === normalizedCount) {
      return;
    }

    this.updateCart(
      {
        ...current,
        numberOfItems: normalizedCount,
      },
      current.session?.uid ?? localStorage.getItem('hypenationSessionId') ?? ''
    );

    this.cartUpdateSubject.next();
  }

  // ────────────────────────────────────────────────────────────────────
  // Persistence helpers
  // ────────────────────────────────────────────────────────────────────

  /**
   * Set the in-memory cart and persist it to storage in lockstep.
   */
  private updateCart(cart: Cart, sessionId: string): void {
    this._cart.set(cart);
    this.persistToStorage({ sessionId, cart });
  }

  /**
   * Read the persisted cart back into the in-memory signal at construction
   * time. Drops the entry if it belongs to a different session than the
   * one currently in localStorage (e.g. after `clearSessionId()`).
   */
  private hydrateFromStorage(): void {
    try {
      const raw = localStorage.getItem(CartStateService.CART_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as PersistedCart;
      if (!parsed?.cart?.uid) {
        this.removeFromStorage();
        return;
      }

      const currentSessionId = localStorage.getItem('hypenationSessionId');
      // If we have a current session ID and the persisted cart was bound
      // to a different one, the persisted cart is stale — drop it.
      if (
        currentSessionId &&
        parsed.sessionId &&
        parsed.sessionId !== currentSessionId
      ) {
        this.removeFromStorage();
        return;
      }

      this._cart.set(parsed.cart);
    } catch (err) {
      console.warn('Failed to hydrate cart from storage:', err);
      this.removeFromStorage();
    }
  }

  private persistToStorage(entry: PersistedCart): void {
    try {
      localStorage.setItem(
        CartStateService.CART_STORAGE_KEY,
        JSON.stringify(entry)
      );
    } catch (err) {
      console.warn('Failed to persist cart to storage:', err);
    }
  }

  private removeFromStorage(): void {
    try {
      localStorage.removeItem(CartStateService.CART_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}
